
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Chip,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { fetchPaymentMethods, deletePaymentMethod } from '../api/paymentApi';
import usePaymentStore from '../../../store/paymentStore';
import AddPaymentMethodForm from './AddPaymentMethodForm';

const PaymentMethodsList: React.FC = () => {
  const { paymentMethods, isLoading, error } = usePaymentStore();
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState<string | null>(null);
  const [addPaymentDialogOpen, setAddPaymentDialogOpen] = useState(false);

  // Fetch payment methods on component mount
  useEffect(() => {
    const loadPaymentMethods = async () => {
      try {
        await fetchPaymentMethods();
      } catch (err) {
        // Error is handled in the API function
      }
    };
    
    loadPaymentMethods();
  }, []);

  // Handle deleting a payment method
  const handleDeleteMethod = async () => {
    if (!methodToDelete) return;
    
    try {
      await deletePaymentMethod(methodToDelete);
      setMethodToDelete(null);
      setDeleteDialogOpen(false);
    } catch (err) {
      // Error is handled in the API function
    }
  };

  // Get card icon based on brand
  const getCardIcon = (brand: string) => {
    return <CreditCardIcon />;
  };

  // Format card for display
  const formatCardInfo = (method: any) => {
    return `${method.brand} •••• ${method.last4} (${method.expiryMonth}/${method.expiryYear})`;
  };

  return (
    <Paper elevation={1} sx={{ mb: 4, p: 0 }}>
      <Box p={3}>
        <Typography variant="h6" gutterBottom>
          Payment Methods
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
      
      <Divider />
      
      {isLoading && !paymentMethods.length ? (
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {paymentMethods.length === 0 ? (
            <ListItem>
              <ListItemText
                primary="No payment methods found"
                secondary="Add a payment method to enable payments for API usage"
              />
            </ListItem>
          ) : (
            paymentMethods.map((method) => (
              <React.Fragment key={method.id}>
                <ListItem>
                  <Box mr={2} display="flex" alignItems="center">
                    {getCardIcon(method.brand)}
                  </Box>
                  
                  <ListItemText
                    primary={formatCardInfo(method)}
                    secondary={`Added on ${new Date(parseInt(method.id.split('_')[1]) * 1000).toLocaleDateString()}`}
                  />
                  
                  {method.isDefault && (
                    <Chip
                      size="small"
                      label="Default"
                      color="primary"
                      icon={<CheckIcon />}
                      sx={{ mr: 2 }}
                    />
                  )}
                  
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => {
                        setMethodToDelete(method.id);
                        setDeleteDialogOpen(true);
                      }}
                      disabled={method.isDefault}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))
          )}
          
          <ListItem>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setAddPaymentDialogOpen(true)}
              fullWidth
              sx={{ py: 1 }}
            >
              Add Payment Method
            </Button>
          </ListItem>
        </List>
      )}

      {/* Delete Payment Method Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Payment Method</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this payment method? 
            You will no longer be able to use it for purchases.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteMethod}
            color="error"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Payment Method Dialog */}
      <Dialog 
        open={addPaymentDialogOpen} 
        onClose={() => setAddPaymentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Payment Method</DialogTitle>
        <DialogContent>
          <AddPaymentMethodForm 
            onSuccess={() => setAddPaymentDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default PaymentMethodsList;
